import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section,
} from "../../components/Primitives";
import { getLabs, verifyCertificate, apiError } from "../../lib/api";
import { useSEO } from "../../lib/seo";
import { AnimatePresence, Reveal, Stagger, StaggerItem, motion, panelSwap } from "../../lib/motion";

export default function VerifyCertificate() {
  const [lab, setLab] = useState("GIA");
  const [num, setNum] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({ queryKey: ["labs"], queryFn: getLabs });

  useSEO({
    title: "Verify a Gemstone Certificate — Any Lab, Any Seller",
    description:
      "Enter any report number from GIA, IGI, GTL Jaipur, GRS, SSEF, Gübelin, AGL, Lotus or GIT and we take you to that laboratory's own verification page. We store nothing and verify nothing ourselves.",
    canonical: "/tools/verify-a-certificate",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How do I verify a gemstone certificate in India?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Open the issuing laboratory's own website and enter the report number there. A QR code or link that opens the seller's website instead of the laboratory's is the single most common tell of a worthless certificate.",
          },
        },
        {
          "@type": "Question",
          name: "Is there a government approved gemstone certificate in India?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No. There is no government gemstone certification scheme in India. The Gem Testing Laboratory in Jaipur operates under the Gem & Jewellery Export Promotion Council, which is sponsored by the Ministry of Commerce & Industry — a real affiliation, and not the same thing as a government certificate.",
          },
        },
      ],
    },
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setResult(await verifyCertificate({ lab, report_number: num }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Verify a certificate" }]} />
      </Container>

      <Section className="border-b border-rule pb-12">
        <Container className="grid items-start gap-12 lg:grid-cols-[1fr_0.85fr] lg:gap-16">
          <div>
            <Eyebrow>Including one you didn't buy from us</Eyebrow>
            <H1 className="mt-5">Verify any gemstone certificate</H1>
            <Lede className="mt-6">
              Choose the laboratory, enter the report number, and we'll take you to that laboratory's own
              verification page. We don't hold a copy. We can't edit it. That's the entire point.
            </Lede>
          </div>

          <form onSubmit={submit} className="card-s elev h-fit p-6 lg:sticky lg:top-28 lg:p-8" data-testid="verify-form">
            <label className="block">
              <span className="field-label">Laboratory</span>
              <select value={lab} onChange={(e) => setLab(e.target.value)} className="field ui" data-testid="verify-lab">
                {(data?.items || []).map((l) => (
                  <option key={l.code} value={l.code}>{`${l.code} — ${l.name}`}</option>
                ))}
                <option value="OTHER">My lab isn't listed</option>
              </select>
            </label>
            <label className="mt-4 block">
              <span className="field-label">Report number</span>
              <input
                required
                value={num}
                onChange={(e) => setNum(e.target.value)}
                placeholder="e.g. 2215467890"
                className="field num"
                data-testid="verify-report-number"
              />
            </label>
            <button type="submit" disabled={busy} className="btn btn-primary mt-5 w-full" data-testid="verify-submit">
              {busy ? "Looking up the laboratory…" : "Take me to the laboratory's site"}
            </button>

            <AnimatePresence>
              {result && (
                <motion.div
                  key="verify-result"
                  variants={panelSwap}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  className="mt-6 border-t border-rule pt-5"
                  data-testid="verify-result"
                >
                  <p className="ui text-sm leading-[1.7] text-dim">{result.message}</p>
                  {result.found && (
                    <a
                      href={result.verify_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary mt-4 w-full"
                      data-testid="verify-open-lab"
                    >
                      Open {result.lab.domain} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  <p className="ui mt-4 text-xs text-verify">
                    Stored on our side: nothing. We are deliberately not in the loop.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </Container>
      </Section>

      <Section className="border-b border-rule bg-surface">
        <Stagger className="mx-auto grid w-full max-w-[1320px] gap-12 px-5 sm:px-8 lg:grid-cols-3 lg:gap-16 lg:px-12" stagger={0.08}>
          <StaggerItem>
            <H2>What a real report must state</H2>
            <ul className="ui mt-6 space-y-2.5 text-sm leading-[1.6] text-dim">
              {(data?.must_state || []).map((x) => <li key={x}>· {x}</li>)}
            </ul>
          </StaggerItem>
          <StaggerItem>
            <H2>What tells you a report is worthless</H2>
            <ul className="ui mt-6 space-y-2.5 text-sm leading-[1.6] text-dim">
              {(data?.worthless_tells || []).map((x) => <li key={x}>· {x}</li>)}
            </ul>
          </StaggerItem>
          <StaggerItem>
            <H2>Laboratories we will not accept</H2>
            <p className="ui mt-6 text-sm leading-[1.7] text-dim">
              We name them, because a page that says "we only use trusted labs" without saying which ones
              is worth nothing.
            </p>
            <Link to="/about/labs-we-refuse" className="link-u ui mt-5 inline-block text-sm">
              Read the list →
            </Link>
          </StaggerItem>
        </Stagger>
      </Section>

      <Section>
        <Container className="measure">
          <Reveal>
            <H3>Why we built this</H3>
            <p className="ui mt-4 text-sm leading-[1.7] text-dim">
              The most common way people are cheated in this market is with a certificate from a laboratory
              that either doesn't exist or will certify anything. We would rather you were able to check any
              seller than have you trust us on faith.
            </p>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
